import type { Filter, FilterDataMultiSelect } from '@/app/common/filters/core/filtersTypes.ts';
import { FilterMultiSelectPill } from '@/app/common/filters/types/multiSelect/FilterMultiSelectPill.ts';
import type { FilterTypeBehaviour } from '@/app/common/filters/types/filterTypeDefinition.ts';
import { optionChips } from '@/app/common/filters/types/optionChips.ts';
import { isFilterThatCanFilterItself } from '@/app/common/filters/core/filterDefinitions.ts';
import { filterOptions, isOptionSelected } from '@/app/common/filters/core/filterOptionSelection.ts';

const dataOf = (filter: Filter) => filter.data as FilterDataMultiSelect;

export const multiSelectType: FilterTypeBehaviour = {
  chips: optionChips,
  pillLabels: FilterMultiSelectPill,
  serialise: (filter) => {
    const data = dataOf(filter);
    const selectedValues = data.options.reduce(
      (result: string[], option) =>
        option.selected && option.value !== data.defaultValue ? result.concat(option.value) : result,
      []
    );

    return selectedValues.length > 0 ? selectedValues.join(',') : undefined;
  },
  isSelected: (filter) => dataOf(filter).options.some(isOptionSelected),
  resetValue: (filter) => ({
    ...filter,
    data: {
      ...dataOf(filter),
      options: dataOf(filter).options.map((option) => ({ ...option, selected: !!option.isDefault })),
    },
  }),
  leadingSelection: (filters, selectionOrder) => {
    const selected = filters.flatMap((filter) =>
      filterOptions(filter)
        .filter(isOptionSelected)
        .map((option) => ({ filter, option }))
    );
    const leadingValue = selectionOrder?.find((value) => selected.some(({ option }) => option.value === value));

    return leadingValue ? selected.find(({ option }) => option.value === leadingValue) : selected[0];
  },
  leadingLabel: (filters, _t, _localization, selectionOrder) =>
    multiSelectType.leadingSelection?.(filters, selectionOrder)?.option.label,
  extraCount: (filters) => filters.flatMap(filterOptions).filter(isOptionSelected).length - 1,
  selectedOptions: (filter) => dataOf(filter).options.filter(isOptionSelected),
  normaliseDraft: (filter) => {
    if (isFilterThatCanFilterItself([filter])) return filter;

    const candidates = dataOf(filter).options.filter((option) => !option.isDefault);
    const exhaustive = candidates.length > 1 && candidates.every((option) => option.selected);

    if (!exhaustive) return filter;

    return {
      ...filter,
      data: {
        ...dataOf(filter),
        options: dataOf(filter).options.map((option) => ({ ...option, selected: !!option.isDefault })),
      },
    };
  },
};
